import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { satisfiesAny, useRoles } from '../auth/roles';
import { moduleRegistry, type ModuleMount } from '../nav/registry';
import { useConfirmNavigate } from '../nav/unsaved-guard';
import { cn } from '../lib/cn';

/** Persisted collapse choice. Same hand-rolled shape as `mediaLibraryView` /
 *  `marketing-messages-view` — there is no shared `usePersistedState` helper and
 *  a third one-off case is not the moment to invent one. */
const STORAGE_KEY = 'admin-nav-collapsed';

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    // localStorage may be unavailable (private mode) — the choice just won't persist.
    return false;
  }
}

function writeCollapsed(next: boolean): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
  } catch {
    /* ignore — see readCollapsed */
  }
}

/** True when `pathname` is the module's index or anywhere inside it. */
function isInside(pathname: string, module: ModuleMount): boolean {
  return pathname === module.path || pathname.startsWith(`${module.path}/`);
}

/**
 * `text-lg` is 14px here, not Tailwind's stock 18px — the theme's scale is
 * WP-admin-anchored (`--text-lg: 14px`, `styles/app.css`). 14px/400 IS wp-admin's
 * top-level menu size, measured off prod (`#adminmenu .wp-menu-name`); children
 * get `text-base` = 13px, its submenu size. Both were `text-sm` (12px), which
 * read a step small beside the screen we are replacing.
 */
const ROW =
  'flex items-center gap-2 px-4 py-2 text-lg no-underline text-chrome-fg hover:bg-chrome-hover hover:text-white';

/**
 * Side nav rendered from the module registry (B6), filtered to the entries the
 * current user's roles satisfy — so an `operator` never sees the admin-only
 * "User Management" link. The route guard (RequireRole) still enforces it; this
 * is the matching UX affordance. WP-admin-familiar dark menu (chrome tokens).
 *
 * L8 round 11 (items 3/6/7/9): each row carries a lucide glyph mirroring Play's
 * dashicon, the ACTIVE module expands its `children` indented beneath it (Play's
 * wp-admin submenu model, replacing the five in-page tab bars), and the rail
 * folds to WP's 36px icon-only width via a "Collapse menu" button — rendered
 * FIRST rather than last (round 11b; see the comment on the button).
 *
 * ⚠ WP's hover FLY-OUT submenu for non-active menus is deliberately NOT ported:
 * it needs positioned overlays and buys one click. Consequence to accept — from
 * inside Offers, reaching Popcorn Messages is two clicks, not one.
 */
export function SideNav() {
  const roles = useRoles();
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  // A dirty editor (useUnsavedChangesGuard) asks before a rail click discards
  // its edits — the WorkspaceGroupsRail idiom, shell-wide (ADR-030).
  const confirmNavigate = useConfirmNavigate();

  const visible = moduleRegistry.filter((module) => satisfiesAny(roles, module.requiredRoles));

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    writeCollapsed(next);
  };

  return (
    <nav
      className={cn('shrink-0 bg-chrome pt-2 flex flex-col', collapsed ? 'w-9' : 'w-52')}
      aria-label="Main"
    >
      {/*
        FIRST item, not WP's last (round 11b). WP can afford last because its
        `#adminmenuwrap` is independently scrollable chrome; this nav is a plain
        flex child of a `flex-1` row with no sticky positioning, so it stretches to
        the height of whichever is taller — the rail or the page. Bottom-pinned, on
        any tall page (the instances table, the messages grid) the button sat at the
        bottom of the DOCUMENT and you had to scroll the whole page to reach it, and
        expanding Marketing's ten children made the rail taller still.
        A `sticky top-0 h-screen overflow-y-auto` rail with the button last would be
        the closer-to-WP alternative; top placement was the operator's call.
      */}
      <button
        type="button"
        onClick={toggle}
        aria-expanded={!collapsed}
        className={cn(
          ROW,
          'w-full bg-transparent border-0 border-b border-chrome-hover cursor-pointer',
          collapsed && 'justify-center px-0',
        )}
      >
        {collapsed ? <ChevronRight size={16} className="shrink-0" /> : <ChevronLeft size={16} className="shrink-0" />}
        <span className={collapsed ? 'sr-only' : undefined}>{collapsed ? 'Expand menu' : 'Collapse menu'}</span>
      </button>
      <ul className="m-0 p-0 list-none">
        {visible.map((module, index) => {
          const Icon = module.icon;
          // Children render only for the module you are in — see the fly-out note above.
          const children = collapsed || !isInside(pathname, module) ? [] : (module.children ?? []);
          // One heading per RUN of same-section modules; collapsed rail has no
          // room for it (the icons still group visually by order).
          const sectionHeading =
            !collapsed && module.section && module.section !== visible[index - 1]?.section
              ? module.section
              : null;
          return (
            <li key={module.path} className="relative">
              {sectionHeading ? (
                <div className="px-4 pt-4 pb-1 text-[10px] font-semibold tracking-wider text-chrome-fg-dim uppercase">
                  {sectionHeading}
                </div>
              ) : null}
              <NavLink
                to={module.path}
                onClick={(e) => {
                  if (!confirmNavigate()) e.preventDefault();
                }}
                className={({ isActive }) =>
                  cn(
                    ROW,
                    collapsed && 'justify-center px-0',
                    module.note && 'text-danger',
                    isActive && 'bg-primary text-white font-semibold',
                  )
                }
              >
                {Icon ? <Icon size={16} className="shrink-0" /> : null}
                {/* Collapsed keeps the name for assistive tech (and for
                    `getByRole('link', {name})`) — `css: false` in the vitest
                    config means no test can assert "visually hidden", so the
                    accessible name must be identical in both states. */}
                <span className={collapsed ? 'sr-only' : undefined}>{module.navLabel}</span>
              </NavLink>
              {/* OUTSIDE the NavLink on purpose: inside, the marker would join
                  the link's accessible name ("Shop PreviewPH2") and rename the
                  module for every query and screen reader. `pointer-events-none`
                  keeps the whole row clickable. */}
              {module.note && !collapsed ? (
                <span className="pointer-events-none absolute top-2 right-4 text-[10px] font-semibold text-danger">
                  {module.note}
                </span>
              ) : null}
              {children.length > 0 ? (
                <ul className="m-0 p-0 list-none bg-chrome-hover/40">
                  {children
                    .filter((child) => satisfiesAny(roles, child.requiredRoles ?? module.requiredRoles))
                    .map((child) => (
                      <li key={child.path} className="relative">
                        <NavLink
                          // `end` on the index child so it doesn't stay active on
                          // every sibling path (e.g. /leaderboards vs /leaderboards/templates).
                          to={child.path === '' ? module.path : `${module.path}/${child.path}`}
                          end={child.path === ''}
                          onClick={(e) => {
                            if (!confirmNavigate()) e.preventDefault();
                          }}
                          className={({ isActive }) =>
                            cn(
                              // 13px — WP's submenu size. See the note on ROW.
                            'block pl-10 pr-4 py-1.5 text-base no-underline text-chrome-fg-dim hover:text-white',
                              isActive && 'text-white font-semibold',
                            )
                          }
                        >
                          {child.navLabel}
                        </NavLink>
                        {child.note ? (
                          <span className="pointer-events-none absolute top-1.5 right-4 text-[10px] font-semibold text-danger">
                            {child.note}
                          </span>
                        ) : null}
                      </li>
                    ))}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
